import { useSyncExternalStore } from 'react'
import {
  addCarConnectionChangedListener,
  getNativeCarConnectionState,
} from '../services/voice-call/native-call'
import { isNativeApp } from '../utils/platform'

/**
 * Is this device attached to a car right now (Apple CarPlay scene connected /
 * Android Auto projection live / car UI mode / car-typed audio output)?
 *
 * Drives the in-car VIDEO pause only: while connected, a video call still
 * connects but this device never starts its camera and never renders remote
 * video. Audio-only car calls are untouched — nothing here feeds the audio
 * route / ringback paths (those read the native car signals directly).
 *
 * Native pushes `carConnectionChanged`; the store also re-queries on demand
 * (call join) because Android's car UI-mode / car audio output can change
 * without a projection-session event.
 */

let carConnected = false
let initialized = false
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function setCarConnected(next: boolean): void {
  if (carConnected === next) return
  carConnected = next
  emit()
}

/** Last known attach state (synchronous — see refreshCarConnection for a re-query). */
export function isCarConnected(): boolean {
  return carConnected
}

/** Re-query native and update the store. Resolves the fresh state. */
export async function refreshCarConnection(): Promise<boolean> {
  if (!isNativeApp()) return false
  const connected = await getNativeCarConnectionState()
  setCarConnected(connected)
  return connected
}

/**
 * Start the native listener + seed the state. Idempotent; call once from the
 * voice-call controller on mount. No-op on web.
 */
export function initCarConnectionStore(): void {
  if (initialized || !isNativeApp()) return
  initialized = true
  void addCarConnectionChangedListener((connected) => setCarConnected(connected))
  void refreshCarConnection()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useCarConnected(): boolean {
  return useSyncExternalStore(subscribe, isCarConnected, isCarConnected)
}

/** Test-only: reset module state between tests. */
export function resetCarConnectionStoreForTests(): void {
  carConnected = false
  initialized = false
  listeners.clear()
}
